import { useCallback } from 'react';
import { useRouter } from 'expo-router';
import {
  defineMessages,
  FormattedMessage,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';
import { StyleSheet } from 'react-native';

import { BodyText, Button, Icon, TextLink, theme, TitleText, VStack } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { ReceiptAnimatedWrapper } from '../ReceiptAnimatedWrapper';

export const ReceiptClaimSuccess = (props: ReceiptClaimSuccessProps) => {
  const { name } = props;

  const router = useRouter();
  const isLoyaltyUpdatesEnabled = useFeatureFlag('CELS-5711-order-loyalty-updates-enabled');

  const goToPointsHistory = useCallback(() => {
    router.navigate(isLoyaltyUpdatesEnabled ? '/loyalty' : '/rewards');
  }, [isLoyaltyUpdatesEnabled, router]);

  const goToHome = useCallback(() => {
    router.navigate('/');
  }, [router]);

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <VStack style={styles.container}>
      <VStack style={styles.titleContainer}>
        <ReceiptAnimatedWrapper animationDelay={0}>
          <Icon name="IconSgRewardKaleFill" width={67} height={67} />
        </ReceiptAnimatedWrapper>

        <ReceiptAnimatedWrapper animationDelay={200}>
          <TitleText sizeMatch={['24', '40']} style={styles.text}>
            {name ? (
              <FormattedMessage {...messages.welcome} values={{ name }} />
            ) : (
              <FormattedMessage {...messages.welcomeMissingName} />
            )}
          </TitleText>
        </ReceiptAnimatedWrapper>

        <ReceiptAnimatedWrapper animationDelay={200}>
          <BodyText style={styles.text} sizeMatch={['16', '18']}>
            <FormattedMessage {...messages.description} />
          </BodyText>
        </ReceiptAnimatedWrapper>
      </VStack>

      <VStack>
        <ReceiptAnimatedWrapper animationDelay={400}>
          <Button
            palette="kaleLightest"
            size="large-wide"
            onPress={goToPointsHistory}
            style={styles.cta}
          >
            <FormattedMessage {...messages.viewPointsHistory} />
          </Button>
        </ReceiptAnimatedWrapper>

        <ReceiptAnimatedWrapper animationDelay={400}>
          <TextLink sizeMatch={['16']} style={styles.link} onPress={goToHome}>
            <FormattedMessage {...messages.backToHome} />
          </TextLink>
        </ReceiptAnimatedWrapper>
      </VStack>
    </VStack>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing['4'],
    paddingVertical: theme.spacing['12'],
  },
  titleContainer: {
    alignItems: 'center',
  },
  text: {
    textAlign: 'center',
  },
  link: {
    color: theme.colors.SPINACH,
    textAlign: 'center',
  },
  cta: {
    width: 300,
    maxWidth: '100%',
    paddingHorizontal: theme.spacing['4'],
  },
});

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly welcome: { readonly name: MessageValue };
  readonly welcomeMissingName: NoMessageValues;
  readonly description: NoMessageValues;
  readonly viewPointsHistory: NoMessageValues;
  readonly backToHome: NoMessageValues;
}>({
  welcome: {
    defaultMessage: 'Welcome to the sweetlife, {name}!',
    description: 'Receipt Claim | Success View | Welcome user title',
  },
  welcomeMissingName: {
    defaultMessage: 'Welcome to the sweetlife!',
    description: 'Receipt Claim | Success View | Welcome user title | Missing name edge case',
  },
  description: {
    defaultMessage: 'Points were awarded to your account',
    description: 'Receipt Claim | Success View | Points awarded description',
  },
  viewPointsHistory: {
    defaultMessage: 'View your points history',
    description: 'Receipt Claim | Success View | View point history CTA',
  },
  backToHome: {
    defaultMessage: 'Back to home',
    description: 'Receipt Claim | Success View | Back to home link',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ReceiptClaimSuccessProps = {
  name?: string | null;
};
